// src/pages/AdminExperienceReviews.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaArchive,
  FaCheck,
  FaChevronDown,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaFilter,
  FaRedo,
  FaRegClock,
  FaSearch,
  FaShieldAlt,
  FaSpinner,
  FaStar,
  FaTimes,
  FaUser,
} from 'react-icons/fa';
import { supabase, SUPABASE_URL } from '../services/supabaseClient';

type ReviewStatus = 'pending_review' | 'approved' | 'rejected' | 'archived';

type ExperienceReview = {
  id: string;
  user_id?: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
  source_type?: string | null;
  source_id?: string | null;
  voucher_id?: string | null;
  voucher_code?: string | null;
  reservation_id?: string | null;
  order_id?: string | null;
  business_id?: string | null;
  rating?: number | null;
  review_title?: string | null;
  comment?: string | null;
  is_public?: boolean | null;
  status?: ReviewStatus | string | null;
  language?: string | null;
  metadata?: Record<string, any> | null;
  business_name?: string | null;
  business_slug?: string | null;
  business_region?: string | null;
  partner_response?: string | null;
  partner_response_at?: string | null;
  featured?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type ReviewStats = {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  archived: number;
  public_count: number;
  average_rating: number;
};

const STATUS_LABELS: Record<string, string> = {
  all: 'Tous',
  pending_review: 'À valider',
  approved: 'Approuvés',
  rejected: 'Rejetés',
  archived: 'Archivés',
};

const STATUS_STYLES: Record<string, string> = {
  pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  archived: 'bg-slate-100 text-slate-600 border-slate-200',
};

const safeText = (value: unknown, fallback = '—') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const ratingStars = (rating?: number | null) => {
  const value = Math.max(0, Math.min(5, Number(rating || 0)));
  return Array.from({ length: 5 }).map((_, index) => (
    <FaStar key={index} className={index < value ? 'text-amber-400' : 'text-slate-200'} />
  ));
};

export default function AdminExperienceReviews() {
  const [reviews, setReviews] = useState<ExperienceReview[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending_review');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<ExperienceReview | null>(null);
  const [adminNote, setAdminNote] = useState('');

  const anonKey = (import.meta as any)?.env?.VITE_SUPABASE_ANON_KEY || '';

  const callAdminReviews = useCallback(async (body: Record<string, unknown>) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (!accessToken) {
      throw new Error('Session admin introuvable. Déconnecte-toi puis reconnecte-toi au portail admin.');
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-experience-reviews`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.ok === false) {
      throw new Error(payload?.details || payload?.error || `Erreur admin avis HTTP ${response.status}`);
    }

    return payload;
  }, [SUPABASE_URL, anonKey]);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const payload = await callAdminReviews({
        action: 'list',
        status: statusFilter,
        search: searchTerm,
        limit: 100,
      });

      setReviews(Array.isArray(payload?.reviews) ? payload.reviews : []);
      setStats(payload?.stats || null);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Impossible de charger les avis.');
    } finally {
      setLoading(false);
    }
  }, [callAdminReviews, searchTerm, statusFilter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const visibleReviews = useMemo(() => reviews, [reviews]);

  const updateReview = async (review: ExperienceReview, updates: { status?: ReviewStatus; is_public?: boolean; business_name?: string | null; business_slug?: string | null; business_region?: string | null; partner_response?: string | null; featured?: boolean }) => {
    setActionLoading(review.id);
    setErrorMessage(null);

    try {
      const payload = await callAdminReviews({
        action: 'update',
        review_id: review.id,
        ...updates,
        admin_note: adminNote,
      });

      const updated = payload?.review as ExperienceReview | undefined;
      if (updated?.id) {
        setReviews((items) => items.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
        setSelectedReview((current) => (current?.id === updated.id ? { ...current, ...updated } : current));
      }

      setAdminNote('');
      await fetchReviews();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Action impossible.');
    } finally {
      setActionLoading(null);
    }
  };

  const togglePublic = (review: ExperienceReview) => {
    updateReview(review, { is_public: !review.is_public });
  };

  return (
    <div className="animate-in fade-in space-y-6">
      <div className="rounded-[2rem] bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 p-6 md:p-8 text-white shadow-2xl overflow-hidden relative">
        <div className="absolute -top-24 right-10 h-64 w-64 rounded-full bg-blue-500/25 blur-3xl" />
        <div className="absolute -bottom-28 left-10 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-blue-100 mb-4">
              <FaShieldAlt /> Modération Forfeo
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">Avis après expérience</h2>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-slate-300 font-medium leading-relaxed">
              Valide, rends publics ou archive les avis reçus depuis la page /avis-experience. Les avis restent invisibles publiquement tant qu’ils ne sont pas approuvés.
            </p>
          </div>

          <button
            onClick={fetchReviews}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-xs font-black uppercase tracking-widest text-slate-950 shadow-xl hover:bg-blue-50 disabled:opacity-60"
          >
            {loading ? <FaSpinner className="animate-spin" /> : <FaRedo />} Rafraîchir
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {[
            ['Total', stats.total, 'bg-slate-900 text-white'],
            ['À valider', stats.pending, 'bg-amber-50 text-amber-700'],
            ['Approuvés', stats.approved, 'bg-emerald-50 text-emerald-700'],
            ['Publics', stats.public_count, 'bg-blue-50 text-blue-700'],
            ['Archivés', stats.archived, 'bg-slate-100 text-slate-700'],
            ['Moyenne', stats.average_rating ? `${stats.average_rating.toFixed(1)}★` : '—', 'bg-white text-slate-900'],
          ].map(([label, value, cls]) => (
            <div key={label} className={`rounded-2xl border border-slate-200 p-4 shadow-sm ${cls}`}>
              <p className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</p>
              <p className="mt-2 text-2xl font-black">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[220px_1fr_auto] items-center">
          <label className="relative block">
            <FaFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="h-12 w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-10 text-sm font-black text-slate-800 outline-none focus:border-blue-400"
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
          </label>

          <label className="relative block">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') fetchReviews();
              }}
              placeholder="Rechercher par courriel, nom, commentaire, code FE..."
              className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-800 outline-none focus:border-blue-400"
            />
          </label>

          <button
            onClick={fetchReviews}
            disabled={loading}
            className="h-12 rounded-2xl bg-slate-950 px-6 text-xs font-black uppercase tracking-widest text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Filtrer
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700">
          {errorMessage}
        </div>
      )}

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-3 p-12 text-slate-500 font-bold">
            <FaSpinner className="animate-spin" /> Chargement des avis...
          </div>
        ) : visibleReviews.length === 0 ? (
          <div className="p-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <FaRegClock />
            </div>
            <h3 className="text-xl font-black text-slate-900">Aucun avis trouvé</h3>
            <p className="mt-2 text-sm font-medium text-slate-500">Change le filtre ou attends de nouveaux avis depuis /avis-experience.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {visibleReviews.map((review) => {
              const isBusy = actionLoading === review.id;
              const status = safeText(review.status, 'pending_review');
              return (
                <div key={review.id} className="p-5 hover:bg-slate-50/70 transition">
                  <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${STATUS_STYLES[status] || STATUS_STYLES.pending_review}`}>
                          {STATUS_LABELS[status] || status}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-600">
                          {review.is_public ? <FaEye /> : <FaEyeSlash />} {review.is_public ? 'Public demandé' : 'Privé'}
                        </span>
                        {review.featured && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-700">
                            <FaStar /> En vedette
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-sm font-black text-slate-800">
                          {ratingStars(review.rating)}
                        </span>
                      </div>

                      <h3 className="text-lg font-black text-slate-950">
                        {safeText(review.review_title, 'Avis Forfeo')}
                      </h3>
                      <p className="mt-2 max-w-4xl whitespace-pre-wrap text-sm font-medium leading-relaxed text-slate-600">
                        {safeText(review.comment, 'Aucun commentaire')}
                      </p>

                      <div className="mt-4 grid gap-2 text-xs font-bold text-slate-500 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="flex items-center gap-2"><FaUser className="text-slate-400" /> {safeText(review.customer_name, 'Client Forfeo')}</div>
                        <div className="flex items-center gap-2"><FaEnvelope className="text-slate-400" /> {safeText(review.customer_email)}</div>
                        <div>Source : <span className="text-slate-800">{safeText(review.source_type)}</span></div>
                        <div>Créé : <span className="text-slate-800">{formatDate(review.created_at)}</span></div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-black text-slate-500">
                        {review.voucher_code && <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-700">Voucher {review.voucher_code}</span>}
                        {review.business_id && <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">Business {String(review.business_id).slice(0, 8)}</span>}
                        {review.reservation_id && <span className="rounded-full bg-purple-50 px-3 py-1 text-purple-700">Réservation {String(review.reservation_id).slice(0, 8)}</span>}
                        {review.order_id && <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">Commande {String(review.order_id).slice(0, 8)}</span>}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 xl:justify-end xl:min-w-[360px]">
                      <button
                        onClick={() => setSelectedReview(review)}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-700 hover:bg-slate-100"
                      >
                        Détails
                      </button>
                      <button
                        onClick={() => updateReview(review, { status: 'approved', is_public: review.is_public ?? true })}
                        disabled={isBusy}
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-700 disabled:opacity-60"
                      >
                        {isBusy ? <FaSpinner className="animate-spin" /> : <FaCheck />} Approuver
                      </button>
                      <button
                        onClick={() => updateReview(review, { status: 'rejected', is_public: false })}
                        disabled={isBusy}
                        className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-xs font-black uppercase tracking-widest text-white hover:bg-rose-700 disabled:opacity-60"
                      >
                        <FaTimes /> Rejeter
                      </button>
                      <button
                        onClick={() => updateReview(review, { status: 'archived', is_public: false })}
                        disabled={isBusy}
                        className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-widest text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        <FaArchive /> Archiver
                      </button>
                      <button
                        onClick={() => togglePublic(review)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-700 hover:bg-blue-50 disabled:opacity-60"
                      >
                        {review.is_public ? <FaEyeSlash /> : <FaEye />} {review.is_public ? 'Masquer' : 'Public'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedReview && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 p-5 backdrop-blur-xl">
              <div>
                <h3 className="text-xl font-black text-slate-950">Détail de l’avis</h3>
                <p className="text-xs font-bold text-slate-500">ID : {selectedReview.id}</p>
              </div>
              <button onClick={() => setSelectedReview(null)} className="rounded-full bg-slate-100 p-3 text-slate-600 hover:bg-rose-50 hover:text-rose-600">
                <FaTimes />
              </button>
            </div>

            <div className="space-y-5 p-6">
              <div className="rounded-3xl bg-slate-50 p-5">
                <div className="mb-3 flex gap-1 text-xl">{ratingStars(selectedReview.rating)}</div>
                <h4 className="text-2xl font-black text-slate-950">{safeText(selectedReview.review_title, 'Avis Forfeo')}</h4>
                <p className="mt-3 whitespace-pre-wrap text-sm font-medium leading-relaxed text-slate-700">{safeText(selectedReview.comment)}</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Client</p>
                  <p className="mt-2 font-black text-slate-900">{safeText(selectedReview.customer_name, 'Client Forfeo')}</p>
                  <p className="text-sm font-bold text-slate-500">{safeText(selectedReview.customer_email)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Statut</p>
                  <p className="mt-2 font-black text-slate-900">{STATUS_LABELS[safeText(selectedReview.status)] || safeText(selectedReview.status)}</p>
                  <p className="text-sm font-bold text-slate-500">{selectedReview.is_public ? 'Peut être public après approbation' : 'Privé'}</p>
                </div>
              </div>

              <div className="rounded-3xl border border-emerald-100 bg-emerald-50/60 p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Réputation publique</p>
                    <p className="mt-1 text-sm font-semibold text-emerald-900">Ces champs enrichissent l'affichage dans /avis-publics.</p>
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-700 shadow-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedReview.featured)}
                      onChange={(event) => setSelectedReview((current) => current ? { ...current, featured: event.target.checked } : current)}
                    />
                    En vedette
                  </label>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <input
                    value={selectedReview.business_name || ''}
                    onChange={(event) => setSelectedReview((current) => current ? { ...current, business_name: event.target.value } : current)}
                    placeholder="Nom public partenaire"
                    className="rounded-2xl border border-emerald-100 bg-white p-3 text-sm font-bold outline-none focus:border-emerald-400"
                  />
                  <input
                    value={selectedReview.business_region || ''}
                    onChange={(event) => setSelectedReview((current) => current ? { ...current, business_region: event.target.value } : current)}
                    placeholder="Région / ville"
                    className="rounded-2xl border border-emerald-100 bg-white p-3 text-sm font-bold outline-none focus:border-emerald-400"
                  />
                  <input
                    value={selectedReview.business_slug || ''}
                    onChange={(event) => setSelectedReview((current) => current ? { ...current, business_slug: event.target.value } : current)}
                    placeholder="Slug public optionnel"
                    className="rounded-2xl border border-emerald-100 bg-white p-3 text-sm font-bold outline-none focus:border-emerald-400"
                  />
                </div>
                <textarea
                  value={selectedReview.partner_response || ''}
                  onChange={(event) => setSelectedReview((current) => current ? { ...current, partner_response: event.target.value } : current)}
                  placeholder="Réponse publique optionnelle du partenaire..."
                  className="mt-3 min-h-[90px] w-full rounded-2xl border border-emerald-100 bg-white p-3 text-sm font-medium outline-none focus:border-emerald-400"
                />
                <button
                  onClick={() => updateReview(selectedReview, {
                    business_name: selectedReview.business_name || null,
                    business_region: selectedReview.business_region || null,
                    business_slug: selectedReview.business_slug || null,
                    partner_response: selectedReview.partner_response || null,
                    featured: Boolean(selectedReview.featured),
                  })}
                  className="mt-3 rounded-2xl bg-emerald-700 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-800"
                >
                  Enregistrer réputation publique
                </button>
              </div>

              <textarea
                value={adminNote}
                onChange={(event) => setAdminNote(event.target.value)}
                placeholder="Note interne Forfeo pour cette action de modération..."
                className="min-h-[110px] w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-medium outline-none focus:border-blue-400"
              />

              <div className="flex flex-wrap gap-3">
                <button onClick={() => updateReview(selectedReview, { status: 'approved', is_public: selectedReview.is_public ?? true })} className="rounded-2xl bg-emerald-600 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-700">Approuver</button>
                <button onClick={() => updateReview(selectedReview, { status: 'rejected', is_public: false })} className="rounded-2xl bg-rose-600 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-rose-700">Rejeter</button>
                <button onClick={() => updateReview(selectedReview, { status: 'archived', is_public: false })} className="rounded-2xl bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-slate-800">Archiver</button>
                <button onClick={() => togglePublic(selectedReview)} className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-xs font-black uppercase tracking-widest text-slate-700 hover:bg-slate-50">{selectedReview.is_public ? 'Masquer public' : 'Marquer public'}</button>
              </div>

              <details className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <summary className="cursor-pointer text-xs font-black uppercase tracking-widest text-slate-500">Métadonnées</summary>
                <pre className="mt-4 overflow-x-auto rounded-xl bg-slate-950 p-4 text-xs text-slate-100">{JSON.stringify(selectedReview, null, 2)}</pre>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
